import type ExcelJS from 'exceljs';

// ── 共通カラー定数 ──────────────────────────────────────────────
export const XL_COLORS = {
  headerBg:   'FF1E3A5F',  // 紺
  headerFg:   'FFFFFFFF',  // 白
  labelBg:    'FFE8EEF6',  // 薄い水色
  rowOdd:     'FFFFFFFF',
  rowEven:    'FFF5F8FC',
  accent:     'FF0EA5E9',  // 空色
  border:     'FFB0BEC5',
  titleBg:    'FF0F2D54',
} as const;

// ── 細線ボーダー ───────────────────────────────────────────────
export const THIN_BORDER: Partial<ExcelJS.Borders> = {
  top:    { style: 'thin', color: { argb: XL_COLORS.border } },
  bottom: { style: 'thin', color: { argb: XL_COLORS.border } },
  left:   { style: 'thin', color: { argb: XL_COLORS.border } },
  right:  { style: 'thin', color: { argb: XL_COLORS.border } },
};

export const MEDIUM_BORDER: Partial<ExcelJS.Borders> = {
  top:    { style: 'medium', color: { argb: XL_COLORS.headerBg } },
  bottom: { style: 'medium', color: { argb: XL_COLORS.headerBg } },
  left:   { style: 'medium', color: { argb: XL_COLORS.headerBg } },
  right:  { style: 'medium', color: { argb: XL_COLORS.headerBg } },
};

// ── セルスタイルヘルパー ────────────────────────────────────────

/** タイトル行スタイル */
export function styleTitle(row: ExcelJS.Row, colCount: number) {
  row.height = 28;
  row.eachCell({ includeEmpty: true }, (cell, col) => {
    if (col > colCount) return;
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: XL_COLORS.titleBg } };
    cell.font  = { bold: true, size: 13, color: { argb: XL_COLORS.headerFg }, name: 'メイリオ' };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    cell.border = THIN_BORDER;
  });
}

/** ヘッダー行スタイル */
export function styleHeader(row: ExcelJS.Row, colCount?: number) {
  row.height = 22;
  row.eachCell({ includeEmpty: true }, (cell, col) => {
    if (colCount && col > colCount) return;
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: XL_COLORS.headerBg } };
    cell.font  = { bold: true, size: 10, color: { argb: XL_COLORS.headerFg }, name: 'メイリオ' };
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    cell.border = THIN_BORDER;
  });
}

/**
 * 注記行スタイル（タイトルの下に置く説明文）。
 * 「区分」列の使い方など、集計時の注意を帳票に残すために使う。
 */
export function styleNoteRow(row: ExcelJS.Row, colCount: number) {
  row.height = 18;
  row.eachCell({ includeEmpty: true }, (cell, col) => {
    if (col > colCount) return;
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: XL_COLORS.labelBg } };
    cell.font = { size: 9, italic: true, color: { argb: 'FF334155' }, name: 'メイリオ' };
    cell.alignment = { horizontal: 'left', vertical: 'middle' };
    cell.border = THIN_BORDER;
  });
}

/** ラベル＋値のペア行スタイル（偶数カラムがラベル、奇数が値） */
export function styleInfoRow(row: ExcelJS.Row, colCount: number) {
  row.height = 18;
  row.eachCell({ includeEmpty: true }, (cell, col) => {
    if (col > colCount) return;
    const isLabel = col % 2 === 1;
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: isLabel ? XL_COLORS.labelBg : XL_COLORS.rowOdd } };
    cell.font  = { bold: isLabel, size: 10, name: 'メイリオ' };
    cell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
    cell.border = THIN_BORDER;
  });
}

/** データ行スタイル（index=0始まりで交互色） */
export function styleDataRow(row: ExcelJS.Row, index: number, colCount?: number) {
  row.height = 18;
  const bg = index % 2 === 0 ? XL_COLORS.rowOdd : XL_COLORS.rowEven;
  row.eachCell({ includeEmpty: true }, (cell, col) => {
    if (colCount && col > colCount) return;
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } };
    cell.font  = { size: 10, name: 'メイリオ' };
    cell.alignment = { vertical: 'middle', wrapText: true };
    cell.border = THIN_BORDER;
  });
}

/** フッター/署名行スタイル */
export function styleFooterRow(row: ExcelJS.Row, colCount?: number) {
  row.height = 20;
  row.eachCell({ includeEmpty: true }, (cell, col) => {
    if (colCount && col > colCount) return;
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: XL_COLORS.labelBg } };
    cell.font  = { bold: true, size: 10, name: 'メイリオ' };
    cell.alignment = { horizontal: 'left', vertical: 'middle' };
    cell.border = THIN_BORDER;
  });
}

// ── ブラウザダウンロード ────────────────────────────────────────
export async function downloadWorkbook(wb: ExcelJS.Workbook, filename: string) {
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── シンプルリスト出力（ヘッダー+データ行のみ） ─────────────────
export async function exportSimpleList(
  headers: { label: string; width: number }[],
  rows: (string | number | null | undefined)[][],
  sheetName: string,
  filename: string,
  titleText?: string,
) {
  const { default: ExcelJS } = await import('exceljs');
  const wb = new ExcelJS.Workbook();
  wb.creator = 'CATMS';
  const ws = wb.addWorksheet(sheetName);
  ws.columns = headers.map(h => ({ width: h.width }));

  let dataStartRow = 1;
  if (titleText) {
    const titleRow = ws.addRow([titleText]);
    ws.mergeCells(1, 1, 1, headers.length);
    styleTitle(titleRow, headers.length);
    ws.addRow([]);
    dataStartRow = 3;
  }

  const headerRow = ws.addRow(headers.map(h => h.label));
  styleHeader(headerRow, headers.length);

  rows.forEach((r, i) => {
    const dr = ws.addRow(r.map(v => v ?? ''));
    styleDataRow(dr, i, headers.length);
  });

  // ヘッダー行を固定し、オートフィルターを付けて閲覧しやすく
  ws.views = [{ state: 'frozen', ySplit: headerRow.number }];
  ws.autoFilter = {
    from: { row: headerRow.number, column: 1 },
    to:   { row: headerRow.number, column: headers.length },
  };

  // 合計行があれば
  ws.getRow(ws.lastRow!.number).height = 18;

  await downloadWorkbook(wb, filename);
}
